import { Component, EventEmitter, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { first } from 'rxjs/operators';
import { BusinessTransactionType } from '../../../../foundation/models/enums/business-transaction-type.enum';
import { Jurisdiction } from '../../../../foundation/models/enums/jurisdiction.enum';
import { BaseComponent } from '../../../../shared/base-components/base.component';
import { SelectListItemModel } from '../../../../shared/models/select-list-item.model';
import { ValidationStateModel } from '../../../../shared/models/validation-results/validation-state.model';
import { ValidationModel } from '../../../../shared/models/validation-results/validation.model';
import { BusinessTransactionService } from '../../../../shared/services/business-transaction/business-transaction.service';
import { VehicleModel } from '../../../../vehicle/models/vehicle.model';
import { TitleOnlyModel } from '../../../models/title-only.model';
import { TitleModel } from '../../../models/title.model';
import { TransactionService } from '../../../services/transaction.service';
import { NMVTISMessageModel } from '../../models/nmvtis-message.model';
import { VehicleNoTitleHistoryModel } from '../../models/vehicleNoTitleHistory.model';
import * as moment from 'moment';

@Component({
    selector: 'vehicle-selection',
    templateUrl: './vehicle-selection.html',
    inputs: ['nmvtisMessageModel'],
    outputs: ['onSaved', 'onCancel', 'onValidationErrors']
})
// should be called only in case of title transactions; Registrations and Permits don't show this component
export class VehicleSelectionComponent extends BaseComponent implements OnInit {

    public vehicleSelectionForm: UntypedFormGroup;
    public dataLoaded = false;
    public nmvtisMessageModel: NMVTISMessageModel;
    public vehicleNoTitleHistoryList: VehicleNoTitleHistoryModel[];
    public selectedVehicle: VehicleNoTitleHistoryModel;
    public onSaved: EventEmitter<boolean> = new EventEmitter<boolean>();
    public onCancel: EventEmitter<boolean> = new EventEmitter<boolean>();
    public onValidationErrors: EventEmitter<ValidationModel> = new EventEmitter<ValidationModel>();
    public vehicle: VehicleModel;
    private title: TitleModel;
    private businessTransactionNumber: number;
    private businessTransactionTypeId: number;
    private transaction: any;
    public previousTitleJurisdictionID: number;
    public previousTitleNumber: string;
    public previousTitleIssueDate: Date;
    public jurisdiction: Jurisdiction = new Jurisdiction();
    public jurisdictions: any;

    constructor(private fb: UntypedFormBuilder,
        private _businessTransactionService: BusinessTransactionService,
        private _transactionService: TransactionService,
        private _router: Router,
        private _route: ActivatedRoute
    ) {
        super();

        this.businessTransactionNumber = +_route.snapshot.params['id']; // grab the business transaction from the route.

        this.vehicleSelectionForm = this.fb.group({
            // "removeIt": ["", Validators.required]
        });

    }

    async ngOnInit() {

        this.jurisdictions = new Array<SelectListItemModel>();
        const jurisdictionList = new Jurisdiction().GetAll();
        for (var i = 0; i < jurisdictionList.length; i++) {
            const j = jurisdictionList[i];
            var jItem = new SelectListItemModel(j.displayValue, j.id.toString());
            this.jurisdictions.push(jItem);
        }

        this.vehicleNoTitleHistoryList = <VehicleNoTitleHistoryModel[]> this.nmvtisMessageModel.nmvtisResponse.vehicleNoTitleHistory;
        this.vehicleNoTitleHistoryList.sort((s1, s2) => s1.titleIssueDate.valueOf() - s2.titleIssueDate.valueOf()); // sort by title issue date

        this.dataLoaded = true;

        if (!this.businessTransactionNumber) { // TODO: verify if this is the right approach
            this.businessTransactionNumber = this.nmvtisMessageModel.businessTransactionId;
        }
        this.businessTransactionTypeId = await this._transactionService.getTransactionTypeByBusinessTransactionId(this.businessTransactionNumber).pipe(first()).toPromise();

        this.transaction = await this._transactionService.getTransaction(this.businessTransactionNumber).pipe(first()).toPromise();
        this.setVehicle();
    }

    private setVehicle(): void {
        const businessTransacion = null;
        const businessTransactionType: BusinessTransactionType = new BusinessTransactionType();
        const selectedTransaction = businessTransactionType.FromId(this.businessTransactionTypeId);
        switch (selectedTransaction) {
            case businessTransactionType.TitleandRegistration:
            case businessTransactionType.TitleOnly:
            case businessTransactionType.ManageLiensandBrands:
                let titleTransaction = <TitleOnlyModel> this.transaction;

                this.title = titleTransaction.title;
                let prevTitle = titleTransaction.previousTitle; // prefer the one with the greater titleIssueDate
                if (this.title) {

                    if (this.title.titleOutOfJurisdiction != null && this.title.titleOutOfJurisdiction.titleNumber != null) {
                        this.previousTitleJurisdictionID = this.title.titleOutOfJurisdiction.jurisdictionID;
                        this.previousTitleNumber = this.title.titleOutOfJurisdiction.titleNumber;
                        this.previousTitleIssueDate = this.title.titleOutOfJurisdiction.titleIssuedDate;
                    }
                    if (prevTitle != null && prevTitle.id > 0) {
                        let shouldTakePrecedence = true;
                        if (this.previousTitleIssueDate) { // titleOutOfJurisdiction was present
                            let aMoment = moment(this.previousTitleIssueDate);
                            let bMoment = moment(prevTitle.titleIssuedDate);
                            if (bMoment.isAfter(aMoment)) {
                                shouldTakePrecedence = true;
                            } else {
                                shouldTakePrecedence = false;
                            }
                        }

                        if (shouldTakePrecedence) {
                            this.previousTitleJurisdictionID = 4; // AZ
                            this.previousTitleNumber = prevTitle.titleNumber;
                            this.previousTitleIssueDate = prevTitle.titleIssuedDate;
                        }
                    }
                }

                this.vehicle = this.title.vehicleOwnership.vehicle; // set vehicle
                break;

            case businessTransactionType.PrintTitle:
                let printTitleTransaction = <TitleOnlyModel> this.transaction;

                this.title = printTitleTransaction.previousTitle ? printTitleTransaction.previousTitle : printTitleTransaction.title;
                let previousTitle = printTitleTransaction.previousTitle; // prefer the one with the greater titleIssueDate
                this.previousTitleJurisdictionID = 4; // AZ
                this.previousTitleNumber = previousTitle.titleNumber;
                this.previousTitleIssueDate = previousTitle.titleIssuedDate;
                this.vehicle = this.title.vehicleOwnership.vehicle; // set vehicle
                break;

            default: // should never come here
                this.vehicle = this.transaction.vehicle;
                break;
        }
        if (this.vehicle) {
            this.vehicle.makeDisplayValue = this.vehicle.makeNCICValue;
        }
    }

    async onAbortTransactionClick() {
        await this._businessTransactionService.expireBusinessTransaction(this.businessTransactionNumber.toString());
        this.onCancelClick(); // close the modal first
        // navigate to the check-in screen and prepopulate with customer data. Make sure customer and agent are same for this purpose.
        const businessTransactionMetaData = (await this._businessTransactionService.getTransactionMetaData(this.businessTransactionNumber.toString()).pipe(first()).toPromise());
        this._router.navigate(['/transaction/agent', businessTransactionMetaData.agentId, { outlets: { 'body': ['serviceslist', businessTransactionMetaData.agentId], 'sidebar': ['servicesadd', businessTransactionMetaData.agentId], 'head': ['info', businessTransactionMetaData.agentId] } }]);
    }

    onCancelClick() {
        this.onCancel.emit(true);
    }

    onSaveClick() {
        var warning = 'NMVTIS active title does not match the active title entered in Max. Confirm title information prior to proceeding.';
        const warnings: string[] = [];
        warnings.push(warning);

        this.createValidationMessages(warnings);

        this.confirmSaved();
    }

    private confirmSaved() {
        this.onSaved.emit(true);
    }

    public onOk() {
        this.confirmSaved();
    }

    private createValidationMessages(messages: string[]) {
        const tempErrors = [];
        const state: ValidationStateModel = new ValidationStateModel();
        state.validationType = 'ConfirmationWarning';
        for (var message of messages) {
            tempErrors.push({ errorMessage: message, state: state });
        }
        return new ValidationModel(tempErrors);
    }
}
